import _ from 'lodash'
import {
	BrowserWindow,
	BrowserWindowSymbol,
	EditorFeatureConfigSymbol,
	isViewerFragmentSymbol,
	CarmiInstance,
	ExperimentsSymbol,
	Experiments,
	RenderDoneObserverSymbol,
	IRenderDoneObserver,
} from '@wix/thunderbolt-symbols'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { Catharsis, CatharsisSymbol } from '@wix/thunderbolt-catharsis'
import type { DSCarmi } from 'thunderbolt-ds-carmi-root'
import { IModelUpdatesHandlersImplementor } from 'ds-feature-model-updates-invoker'
import { name } from '../symbols'
import type {
	ViewerManagerLayoutApiEditorConfig,
	ScreenSizeApi as ScreenSizeApiType,
	BreakpointsRangeChangedHandlers,
} from '../types'

const screenSizeApiFactory = (
	config: ViewerManagerLayoutApiEditorConfig,
	window: NonNullable<BrowserWindow>,
	isViewerFragment: boolean,
	renderDoneObserver: IRenderDoneObserver,
	{ variants: setter }: DSCarmi,
	catharsis: Catharsis,
	experiments: Experiments
): ScreenSizeApiType & IModelUpdatesHandlersImplementor<BreakpointsRangeChangedHandlers> => {
	const { body } = window.document
	let activeBreakpointsIdsCache: Array<string> = []

	const bodySizes = {
		width: body.clientWidth,
		height: body.clientHeight,
	}
	const getBodyWidth = (clientWidth: number) => (config.isMobileView ? 320 : clientWidth)

	const updateActiveBreakpoints = () => {
		const activeBreakpointIds = _.reduce(
			config.breakpointIdToRange,
			(acc, { min = 0, max }, id) => {
				if (bodySizes.width >= min && (!max || bodySizes.width <= max)) {
					acc[id] = true
				}
				return acc
			},
			{} as { [key: string]: boolean }
		)

		const newActiveBps = _.omit(activeBreakpointIds, activeBreakpointsIdsCache)
		if (_.isEmpty(newActiveBps)) {
			return
		}

		activeBreakpointsIdsCache = _.keys(activeBreakpointIds)

		setter.setActiveBreakpoints(activeBreakpointIds)
		catharsis.transaction(({ updateEnvironment }) => updateEnvironment({ activeBreakpoints: activeBreakpointIds }))
	}

	if (!isViewerFragment) {
		const resizeObserver = new window.ResizeObserver!(() => {
			Object.assign(bodySizes, {
				width: body.clientWidth,
				height: body.clientHeight,
			})
			if (experiments['specs.thunderbolt.compute_css_by_active_breakpoints']) {
				updateActiveBreakpoints()
			}
		})
		resizeObserver.observe(body)
	}

	return {
		getScreenSize: () => ({ height: bodySizes.height, width: getBodyWidth(bodySizes.width) }),
		getScreenHeight: () => bodySizes.height,
		getScreenWidth: () => getBodyWidth(bodySizes.width),
		featureName: name,
		handlers: {
			breakpointsRangeChanged: () => {
				if (experiments['specs.thunderbolt.compute_css_by_active_breakpoints']) {
					updateActiveBreakpoints()
				}
			},
		},
	}
}

export const ScreenSizeApi = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		BrowserWindowSymbol,
		isViewerFragmentSymbol,
		RenderDoneObserverSymbol,
		CarmiInstance,
		CatharsisSymbol,
		ExperimentsSymbol,
	],
	screenSizeApiFactory
)
